import React, { forwardRef, useState } from 'react';
import { FormSelect } from './FormSelect';
import styles from './FormMultiSelect.module.css';

const FormMultiSelect = forwardRef(({
    label,
    name,
    value = [],
    onChange,
    options = [],
    error,
    required = false,
    placeholder = 'Select options...',
    disabled = false,
    className = '',
    style = {},
    help,
    ...props
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelectChange = (e) => {
        const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
        // Create a synthetic event compatible with useFormValidation's handleFieldChange
        const syntheticEvent = {
            target: {
                name: name,
                value: selectedValues,
                type: 'select-multiple'
            }
        };
        onChange(syntheticEvent);
    };

    const removeValue = (valueToRemove) => {
        // Ensure value is an array before filtering
        const currentValue = Array.isArray(value) ? value : [];
        const newValue = currentValue.filter(v => v !== valueToRemove);
        // Create a synthetic event compatible with useFormValidation's handleFieldChange
        const syntheticEvent = {
            target: {
                name: name,
                value: newValue,
                type: 'select-multiple'
            }
        };
        onChange(syntheticEvent);
    };

    const getSelectedLabels = () => {
        // Ensure value is an array before calling map
        const valueArray = Array.isArray(value) ? value : [];
        return valueArray.map(val => {
            const option = options.find(opt => opt.value === val);
            return option ? option.label : val;
        });
    };

    const groupedOptions = options.reduce((acc, option) => {
        const group = option.group || 'Other';
        if (!acc[group]) acc[group] = [];
        acc[group].push(option);
        return acc;
    }, {});

    return (
        <div className={`${styles.container} ${className}`} style={style}>
            {label && (
                <label className={styles.label}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}

            {/* Selected values display */}
            <div className={styles.selectedContainer}>
                {(!Array.isArray(value) || value.length === 0) ? (
                    <div className={styles.placeholder}>{placeholder}</div>
                ) : (
                    <div className={styles.selectedTags}>
                        {getSelectedLabels().map((label, index) => (
                            <span key={value[index]} className={styles.tag}>
                                {label}
                                <button
                                    type="button"
                                    onClick={() => removeValue(value[index])}
                                    className={styles.removeTag}
                                    aria-label={`Remove ${label}`}
                                    disabled={disabled}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Multi-select dropdown */}
            <div className={styles.selectWrapper}>
                <select
                    ref={ref}
                    name={name}
                    value={Array.isArray(value) ? value : []}
                    onChange={handleSelectChange}
                    disabled={disabled}
                    required={required && (!Array.isArray(value) || value.length === 0)}
                    className={`${styles.select} ${error ? styles.hasError : ''}`}
                    multiple
                    size={Math.min(8, Object.keys(groupedOptions).length * 3 + 2)}
                    {...props}
                >
                    {Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
                        <optgroup key={groupName} label={groupName}>
                            {groupOptions.map((option) => (
                                <option
                                    key={option.value}
                                    value={option.value}
                                    style={Array.isArray(value) && value.includes(option.value) ? {
                                        backgroundColor: 'var(--color-info-background)',
                                        color: 'var(--color-info)',
                                        fontWeight: '600'
                                    } : {}}
                                >
                                    {Array.isArray(value) && value.includes(option.value) ? `✓ ${option.label}` : option.label}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
            </div>

            {help && (
                <div className={styles.help}>
                    {help}
                </div>
            )}

            {error && (
                <div className={styles.error} role="alert">
                    {error}
                </div>
            )}
        </div>
    );
});

FormMultiSelect.displayName = 'FormMultiSelect';

export { FormMultiSelect };
export default FormMultiSelect;
